"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { useAccountState } from "./components/use-account-state";
import { RunFirstCta } from "./components/run-first-cta";
import { track } from "./lib/track";

type BillingPeriod = "monthly" | "annual";

type PlanProductKey =
  | "pro_monthly"
  | "pro_yearly"
  | "agency_monthly"
  | "agency_yearly";

type TopUpProductKey = "topup_50" | "topup_150" | "topup_500";

type ProductKey = PlanProductKey | TopUpProductKey;

const PLANS: Array<{
  key: "free" | "pro" | "agency";
  name: string;
  tagline: string;
  monthly: number;
  annualMonthly: number;
  annualBilled: number;
  annualSavings: number;
  credits: number;
  creditsLabel: string;
  /** Plain-English translation of what the monthly credits buy. */
  creditsTranslation: string;
  features: string[];
  cta: string;
  badge?: string;
  featured?: boolean;
  productKeyMonthly: PlanProductKey | null;
  productKeyYearly: PlanProductKey | null;
}> = [
  {
    key: "free",
    name: "Free",
    tagline: "Try Snap Site on real pages, no card needed.",
    monthly: 0,
    annualMonthly: 0,
    annualBilled: 0,
    annualSavings: 0,
    credits: 5,
    creditsLabel: "5 credits / month",
    creditsTranslation: "Run a full 5-page desktop audit every month.",
    features: [
      "Desktop-only captures",
      "Up to 5 pages per run",
      "Heuristic + accessibility findings",
    ],
    cta: "Start free",
    productKeyMonthly: null,
    productKeyYearly: null,
  },
  {
    key: "pro",
    name: "Pro",
    tagline: "In-house designers and PMs running weekly audits.",
    monthly: 19,
    annualMonthly: 15.83,
    annualBilled: 190,
    annualSavings: 38,
    credits: 100,
    creditsLabel: "100 credits / month",
    creditsTranslation: "About 10 weekly audits of 5 pages on desktop + mobile.",
    features: [
      "Everything in Free",
      "Mobile captures (Free is desktop-only)",
      "AI-guided interactions",
      "PDF export + shareable links",
      "10 pages per run (Free: 5)",
    ],
    cta: "Get Pro",
    // "Best value" rather than "Most popular": an honest claim tied to the
    // credit math (Pro's cost-per-credit beats Free top-ups) instead of an
    // unbacked usage claim. Still steers choice via the featured styling.
    badge: "Best value",
    featured: true,
    productKeyMonthly: "pro_monthly",
    productKeyYearly: "pro_yearly",
  },
  {
    key: "agency",
    name: "Agency",
    tagline: "Studios reviewing many client sites every month.",
    monthly: 99,
    annualMonthly: 82.5,
    annualBilled: 990,
    annualSavings: 198,
    credits: 600,
    creditsLabel: "600 credits / month",
    creditsTranslation: "About 60 audits a month — agency-scale across a portfolio of client sites.",
    features: [
      "Everything in Pro",
      "Run several audits at once",
      "Priority email support",
    ],
    cta: "Get Agency",
    productKeyMonthly: "agency_monthly",
    productKeyYearly: "agency_yearly",
  },
];

/**
 * Top-up packs. `reviews` translates the credit count into the same unit
 * the pricing-page calculator uses ("typical review" = 10 credits, i.e.
 * 5 pages × desktop + mobile, no AI assistance).
 *
 * `savings` is computed against the smallest pack ($0.24/cr baseline).
 */
const TOP_UPS: Array<{
  productKey: TopUpProductKey;
  credits: number;
  price: number;
  perCredit: string;
  reviews: string;
  savings?: string;
}> = [
  {
    productKey: "topup_50",
    credits: 50,
    price: 12,
    perCredit: "$0.24/cr",
    reviews: "About 5 typical audits",
  },
  {
    productKey: "topup_150",
    credits: 150,
    price: 30,
    perCredit: "$0.20/cr",
    reviews: "About 15 typical audits",
    savings: "Save 17%",
  },
  {
    productKey: "topup_500",
    credits: 500,
    price: 90,
    perCredit: "$0.18/cr",
    reviews: "About 50 typical audits",
    savings: "Save 25%",
  },
];

const API_URL =
  process.env.NEXT_PUBLIC_SNAPSITE_API_BASE ?? "https://api.snapsiteux.com/api";
const CLIENT_ID_KEY = "snap-site:web-client-id";
const PENDING_KEY = "snap-site:web-pending-checkout";

function getClientId(): string {
  if (typeof window === "undefined") return "";
  let id: string | null = null;
  try {
    id = window.localStorage.getItem(CLIENT_ID_KEY);
  } catch {
    id = null;
  }
  if (!id) {
    const random =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
    id = `web-${random}`;
    try {
      window.localStorage.setItem(CLIENT_ID_KEY, id);
    } catch {
      /* ignore */
    }
  }
  return id;
}

function formatPrice(value: number): string {
  if (Number.isInteger(value)) return `$${value}`;
  return `$${value.toFixed(2)}`;
}

export function PricingSection({
  showNarrative = true,
  showCreditSummary = true,
}: {
  showNarrative?: boolean;
  showCreditSummary?: boolean;
} = {}) {
  const [period, setPeriod] = useState<BillingPeriod>("monthly");
  const [pendingKey, setPendingKey] = useState<ProductKey | null>(null);
  const [error, setError] = useState<string>("");
  const [billingBanner, setBillingBanner] = useState<"success" | null>(null);
  const accountState = useAccountState();
  const currentPlan =
    accountState.status === "authenticated" ? accountState.account.plan : null;

  // Detect a post-checkout return (Dodo redirects to /pricing?billing=success
  // after a successful purchase) so we can show a confirmation banner and
  // give entitlements a moment to land before the user clicks anything else.
  // We also strip the query param so refreshing the page doesn't keep flashing
  // the banner.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("billing") === "success") {
      setBillingBanner("success");
      track("Purchase Completed", { surface: "pricing" });
      params.delete("billing");
      const query = params.toString();
      const nextUrl = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
      window.history.replaceState({}, "", nextUrl);
    }
  }, []);

  async function startCheckout(productKey: ProductKey) {
    setError("");
    setPendingKey(productKey);
    try {
      const clientId = getClientId();
      const res = await fetch(`${API_URL}/billing/checkout`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          productKey,
          clientId,
          returnUrl: "https://snapsiteux.com/pricing?billing=success",
        }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || "Could not start checkout. Try again.");
      }
      const data = (await res.json()) as {
        url?: string;
        checkoutId?: string;
        claimToken?: string;
      };
      if (data.checkoutId && data.claimToken) {
        try {
          window.localStorage.setItem(
            PENDING_KEY,
            JSON.stringify({
              checkoutId: data.checkoutId,
              claimToken: data.claimToken,
              productKey,
              createdAt: Date.now(),
            })
          );
        } catch {
          /* ignore */
        }
      }
      if (!data.url) {
        throw new Error("Checkout did not return a link.");
      }
      track("Checkout Started", {
        product_key: productKey,
        surface: "pricing",
      });
      window.location.href = data.url;
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not start checkout. Please try again."
      );
      setPendingKey(null);
    }
  }

  const planCards = useMemo(() => PLANS, []);

  return (
    <section className="lp-section lp-full-bleed lp-pricing" id="pricing">
      {/* ---- Act 1: The narrative lead ---- */}
      {showNarrative ? (
        <div className="lp-pricing-narrative reveal">
          <p className="lp-kicker">Pricing</p>
          <h2>One credit per page, per screen size.</h2>
          <p className="lp-pricing-explain">
            Auditing a homepage on desktop costs 1 credit. Add the mobile
            version and that&rsquo;s 2. Five pages on desktop + mobile
            &mdash; 10 credits. Pick a plan that fits how often you audit,
            and top up any time you need more.
          </p>
        </div>
      ) : null}

      {/* ---- Quick credit explanation — always visible ---- */}
      {showCreditSummary ? (
        <div className="lp-credit-summary reveal" id="credits">
          <span className="lp-credit-summary-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M7 3h8l4 4v14H7z" />
              <path d="M15 3v5h4" />
              <path d="M10 12h6" />
              <path d="M10 16h4" />
            </svg>
          </span>
          <div>
            <b>Simple credit math</b>
            <p>
              One captured page in one screen size uses one credit. Desktop plus
              mobile uses two. AI assistance adds five credits when you need the
              page clicked through before review.
            </p>
          </div>
        </div>
      ) : null}

      {/* ---- Act 2: Billing toggle ---- */}
      <div className="lp-pricing-toggle-wrap reveal">
        <div className="lp-pricing-toggle" role="group" aria-label="Billing period">
          <button
            type="button"
            data-active={period === "monthly"}
            aria-pressed={period === "monthly"}
            onClick={() => setPeriod("monthly")}
          >
            Monthly
          </button>
          <button
            type="button"
            data-active={period === "annual"}
            aria-pressed={period === "annual"}
            onClick={() => setPeriod("annual")}
          >
            Annual
            <em>save 2 months</em>
          </button>
        </div>
      </div>

      {billingBanner === "success" ? (
        <div className="pricing-success-banner" role="status">
          <strong>Thanks for the purchase.</strong> Credits typically land in
          your balance within a minute. Refresh your account page if it
          hasn&rsquo;t updated yet.
          <button
            type="button"
            className="pricing-success-banner-close"
            aria-label="Dismiss"
            onClick={() => setBillingBanner(null)}
          >
            ×
          </button>
        </div>
      ) : null}

      {error ? (
        <p className="pricing-error" role="status">{error}</p>
      ) : null}

      {/* ---- Act 3: Plan cards — clean rows ---- */}
      <div className="lp-plan-grid stagger" id="plans">
        {planCards.map((plan) => {
          const isAnnual = period === "annual";
          const monthlyPrice =
            plan.key === "free"
              ? "$0"
              : formatPrice(isAnnual ? plan.annualMonthly : plan.monthly);
          const billedNote =
            plan.key === "free"
              ? "Free forever"
              : isAnnual
                ? `$${plan.annualBilled} billed yearly`
                : "billed monthly";
          const savingsNote =
            plan.key !== "free" && isAnnual && plan.annualSavings > 0
              ? `Save $${plan.annualSavings} a year`
              : null;
          const productKey: ProductKey | null =
            plan.key === "free"
              ? null
              : isAnnual
                ? plan.productKeyYearly
                : plan.productKeyMonthly;
          const isPending =
            productKey !== null && pendingKey === productKey;
          const isCurrentPlan = currentPlan === plan.key;

          return (
            <article
              key={plan.key}
              id={`plan-${plan.key}`}
              className={[
                "lp-plan-card",
                `lp-plan-${plan.key}`,
                plan.featured ? "lp-plan-featured" : "",
                isCurrentPlan ? "lp-plan-current" : "",
              ].filter(Boolean).join(" ")}
              aria-label={`${plan.name} plan${isCurrentPlan ? " — your current plan" : ""}`}
            >
              {plan.badge && !isCurrentPlan && (
                <span className="lp-plan-badge">{plan.badge}</span>
              )}
              {isCurrentPlan && (
                <span className="lp-plan-badge lp-plan-badge-current">Current plan</span>
              )}

              <h3>{plan.name}</h3>
              <p className="lp-plan-tagline">{plan.tagline}</p>

              <div className="lp-plan-price-row">
                <strong>{monthlyPrice}</strong>
                <span>/mo</span>
              </div>
              <p className="lp-plan-billed">{billedNote}</p>
              {savingsNote ? (
                <p className="lp-plan-savings">{savingsNote}</p>
              ) : null}

              <div className="lp-plan-credits-block">
                <p className="lp-plan-credits-line">{plan.creditsLabel}</p>
                <p className="lp-plan-credits-translation">
                  {plan.creditsTranslation}
                </p>
              </div>

              <ul className="lp-plan-features" role="list">
                {plan.features.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>

              {isCurrentPlan ? (
                <Link className="lp-plan-cta lp-plan-cta-current" href="/account">
                  Manage plan
                </Link>
              ) : plan.key === "free" ? (
                // Run-first for guests (opens the paste-a-URL popover)
                // instead of linking to /audit/new → /login. Signed-in
                // users still go straight to the configurator.
                <RunFirstCta
                  className="lp-plan-cta lp-plan-cta-primary"
                  guestLabel={plan.cta}
                  authedLabel={plan.cta}
                  authedHref="/audit/new"
                />
              ) : (
                <button
                  type="button"
                  className="lp-plan-cta lp-plan-cta-primary"
                  disabled={pendingKey !== null}
                  onClick={() => productKey && startCheckout(productKey)}
                >
                  {isPending ? "Opening checkout..." : plan.cta}
                </button>
              )}

              {!isCurrentPlan ? (
                // Reassure lines now read with parallel rhythm across all
                // plans (two short clauses separated by a period). The
                // previous Free line ("No card. No trial clock.") used
                // fragments while paid plans used full clauses, which
                // made the Free card feel less polished by comparison.
                <p className="lp-plan-reassure">
                  {plan.key === "free"
                    ? "No card needed. Resets monthly."
                    : "Cancel any time. Failed runs auto-refunded."}
                </p>
              ) : null}
            </article>
          );
        })}
      </div>

      {/* ---- Act 4: Top-ups — quiet aside ---- */}
      <div className="lp-topup-aside reveal">
        <h3>Need more credits?</h3>
        <p>
          One-time top-ups stack on top of any plan and stay valid for 12
          months. They&rsquo;re only spent after your monthly subscription
          credits, so nothing goes to waste.
        </p>
        <div className="lp-topup-row">
          {TOP_UPS.map((t) => {
            const isPending = pendingKey === t.productKey;
            const ariaLabel = [
              `Buy ${t.credits}-credit top-up for ${formatPrice(t.price)}`,
              t.savings ? t.savings.toLowerCase() : null,
              "valid 12 months",
            ]
              .filter(Boolean)
              .join(", ");
            return (
              <button
                key={t.productKey}
                type="button"
                className="lp-topup-chip"
                disabled={pendingKey !== null}
                onClick={() => startCheckout(t.productKey)}
                aria-label={ariaLabel}
              >
                {/* Visual children are aria-hidden — the button's aria-label
                    above carries the spoken text for screen readers in a
                    sensible order. The pending state stays announceable. */}
                {t.savings ? (
                  <span className="lp-topup-badge" aria-hidden="true">{t.savings}</span>
                ) : null}
                <span className="lp-topup-price" aria-hidden="true">{formatPrice(t.price)}</span>
                <span className="lp-topup-credits" aria-hidden="true">{t.credits} credits</span>
                <span className="lp-topup-translation" aria-hidden="true">{t.reviews}</span>
                {/* Meta row: per-credit pill on the left, Valid 12
                    months mid-line, "Buy →" tertiary CTA right-
                    aligned via margin-left: auto. Inlining the CTA
                    on the same baseline as the last text line gives
                    it stronger visual weight than the previous
                    new-line position — eye lands on the meta row
                    and the call-to-action sits where the read
                    finishes. */}
                <span className="lp-topup-meta" aria-hidden="true">
                  <em className="lp-topup-per">{t.perCredit}</em>
                  <span>Valid 12 months</span>
                  <span className="lp-topup-cta">
                    {isPending ? (
                      <em className="lp-topup-pending" aria-live="polite">
                        Opening checkout…
                      </em>
                    ) : (
                      <>
                        Buy
                        <span className="lp-topup-cta-arrow" />
                      </>
                    )}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
        {showNarrative ? (
          <div className="lp-pricing-actions lp-pricing-actions-bottom">
            <Link className="lp-btn lp-btn-primary" href="/pricing">
              View pricing details
            </Link>
            <Link className="lp-pricing-link" href="/pricing#credits">
              How credits work
            </Link>
          </div>
        ) : null}
      </div>
    </section>
  );
}
